<template>
  <ModalDialog v-model="showModal" size="md" :showXCloseButton="true" :closeOnEsc="false" :isLoading="isLoading">
    <FormKit v-if="ModalState.NEW_REGO == currentModalState" v-model="newRegoDetails" type="form" @submit="changeRego">
      <RichText :field="changeRegistrationTitle?.label" />
      <RACQFormKit :bindings="formInputData('registrationNumber')" />
      <FormKit
        type="submit"
        :outerClass="showValidationError ? 'mt-8' : 'mt-2'"
        inputClass="btn-md btn w-full btn-primary"
        :label="updateBtn?.label?.value"
        :disabled="isLoading"
      />
    </FormKit>
    <div v-else-if="ModalState.COMPLETE == currentModalState">
      <RichText :field="{ value: completedText }" />
      <button class="mt-2 btn w-full btn-md btn-primary" @click="resetModal">{{ doneBtn?.label?.value }}</button>
    </div>
    <div v-else-if="ModalState.CMO_ERROR == currentModalState">
      <RichText :field="cmoError?.label" />
      <button class="mt-2 btn w-full btn-md btn-primary" @click="resetModal">{{ closeBtn?.label?.value }}</button>
    </div>
  </ModalDialog>
</template>
<script lang="ts" setup>
import { useRouter } from 'vue-router';
import { watch, ref, onMounted, computed } from 'vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import { InsurancePolicy, ISitecoreFormGenericInput, VehicleCoverage } from '@/interfaces';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { getFormInput } from '@/utils/form-input-helper';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import { convertArrayToObject } from '@/utils/data-transforms';
import useFormInputData from '@/composables/useFormInputData';
import { SelfServiceService } from '@/services/store/self-service-service';
import { useAccountStore, useAnalyticsStore } from '@/store';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { FormEvents } from '@/enums/form-events.enum';
import { SelfServiceSessionPages } from '@/enums/self-service-session-pages';
import { ChangeVehicleService } from '@/services/store/change-vehicle.service';
import { JP_GENERIC_CODE_SS } from '@/constants/jeopardy-codes.const';

enum ModalState {
  NEW_REGO = 'newRego',
  COMPLETE = 'complete',
  CMO_ERROR = 'cmoError',
}

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  policy?: InsurancePolicy;
}>();

const changeRegistrationTitle = getFormInput(props.content, 'changeRegistrationTitle');
const updateBtn = getFormInput(props.content, 'updateBtn');
const closeBtn = getFormInput(props.content, 'closeBtn');
const doneBtn = getFormInput(props.content, 'doneBtn');
const cmoError = getFormInput(props.content, 'cmoError');

const formInputs = convertArrayToObject(props.content);
const { formInputData } = useFormInputData(formInputs);

const router = useRouter();
const accountStore = useAccountStore();
const analyticsStore = useAnalyticsStore();

const currentModalState = ref<ModalState>(ModalState.NEW_REGO);
const showModal = ref(false);
const showValidationError = ref(false);
const isLoading = ref(false);
const newRegoDetails = ref<{
  registrationNumber?: string;
}>({});

const completedText = computed(() => {
  const richText = getFormInput(props.content, 'success')?.label?.value as string;
  return replaceBookmark('newRego', richText, newRegoDetails.value.registrationNumber ?? '');
});

watch(
  () => router.currentRoute.value.hash,
  (newVal) => {
    // Start change rego modal
    if (newVal.includes('#changeRego')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      router.currentRoute.value.hash = '';
      showModal.value = true;

      //-------------------------------
      // Push analytics START
      //-------------------------------
      analyticsStore.pushAnalytics({
        event: FormEvents.START,
        form: {
          name: 'Update vehicle registration',
          stepName: 'FormStarted',
          productName: (props.policy?.coverages?.at(0) as VehicleCoverage)?.vehicle?.formattedSummary,
        },
      });

      if (currentModalState.value != ModalState.NEW_REGO) {
        pushJeopardy('Invalid policy status');
      }
      //-------------------------------
      // Push analytics END
      //-------------------------------
    }
  },
  {
    deep: true,
  }
);

watch(showModal, (newVal, oldVal) => {
  if (!newVal && oldVal) resetModal();
});

const resetModal = () => {
  showModal.value = false;
  // Wait 1s for modal close animation to avoid users seeing flash
  setTimeout(() => {
    showValidationError.value = false;
    isLoading.value = false;
    newRegoDetails.value = {};
    if (!props.policy) return (currentModalState.value = ModalState.CMO_ERROR);
    currentModalState.value = ModalState.NEW_REGO;
  }, 1000);
};

onMounted(resetModal);

/**
 * Validate and update rego
 *
 * @param vehicleDetail
 */
const changeRego = async () => {
  try {
    showValidationError.value = false;
    isLoading.value = true;
    const policyNumber = router.currentRoute.value.query.policyNumber as string;
    const coverage = props.policy?.coverages?.[0] as VehicleCoverage | undefined;
    const vehicle = coverage?.vehicle;
    const newRego = newRegoDetails.value.registrationNumber?.toUpperCase().replaceAll(' ', '');

    if (!policyNumber || !coverage?.id || !vehicle || !newRego) {
      throw new Error(JP_GENERIC_CODE_SS);
    }

    newRegoDetails.value.registrationNumber = newRego;

    if (newRego === vehicle.registrationNumber?.toUpperCase()) {
      throw new Error('Same registration number');
    }

    // Create quote session
    const quoteResponse = await SelfServiceService.createQuoteSession(
      policyNumber,
      SelfServiceSessionPages.ChangeVehicleRegistrationPage,
      coverage.id
    );

    if (!quoteResponse?.success) {
      throw new Error(quoteResponse.errors?.[0]?.message ?? JP_GENERIC_CODE_SS);
    }

    // Change registration
    const sessionId = quoteResponse.data;
    const regoResponse = await ChangeVehicleService.changeVehicleRegistration(sessionId, newRego);

    if (!regoResponse?.success) {
      throw new Error(regoResponse.errors?.[0]?.message ?? JP_GENERIC_CODE_SS);
    }
    if (regoResponse.errors && regoResponse.errors.length > 0) {
      throw new Error(regoResponse.errors[0].message ?? JP_GENERIC_CODE_SS);
    }
    const jeopardies = regoResponse.data?.jeopardies;
    if (jeopardies && jeopardies.length > 0) {
      throw new Error(jeopardies[0].code);
    }

    // Analytics
    analyticsStore.pushAnalytics({
      event: FormEvents.COMPLETE,
      form: {
        name: 'Update vehicle registration',
        stepName: 'FormCompleted',
        productName: vehicle.formattedSummary,
      },
    });

    currentModalState.value = ModalState.COMPLETE;
    isLoading.value = false;
  } catch (e: any) {
    currentModalState.value = ModalState.CMO_ERROR;
    showValidationError.value = true;
    isLoading.value = false;
    pushJeopardy(e?.message ?? JP_GENERIC_CODE_SS);
    console.error(e);
  }
};

const pushJeopardy = (error: string) => {
  analyticsStore.pushAnalytics({
    event: FormEvents.JEOPARDY,
    form: {
      name: 'Update vehicle registration',
      formErrorType: error,
      productName: (props.policy?.coverages?.at(0) as VehicleCoverage)?.vehicle?.formattedSummary,
    },
  });
};
</script>
